"use client";

import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { CheckCircle2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { submitLead } from "@/lib/leads";
import { getMessages } from "@/lib/messages";
import { contactFormSchema, type ContactFormValues } from "@/lib/schemas";
import { cn } from "@/lib/utils";

const TYPE_OPTIONS: {
  value: ContactFormValues["type"];
  labelKey: "typeGeneral" | "typeCorporate" | "typePartner";
}[] = [
  { value: "general", labelKey: "typeGeneral" },
  { value: "corporate", labelKey: "typeCorporate" },
  { value: "partner", labelKey: "typePartner" },
];

/** ⚠️ PLACEHOLDER COPY — the radiogroup had no visible label at all in v1. */
const INQUIRY_TYPE_LABEL = "What is this about?";

export function ContactForm() {
  const messages = getMessages();
  const [serverError, setServerError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: { type: "general", name: "", email: "", phone: "", message: "" },
  });

  const selectedType = watch("type");

  async function onSubmit(values: ContactFormValues) {
    setServerError(null);
    const result = await submitLead(values);
    if (result.ok) {
      setSuccess(true);
      return;
    }
    setServerError(
      result.status === 429 ? messages.contact.errorRateLimited : messages.contact.errorGeneric,
    );
  }

  if (success) {
    return (
      <div className="flex flex-col items-start gap-3 border border-edge bg-surface p-8">
        <CheckCircle2 className="h-8 w-8 text-signal" aria-hidden="true" />
        <h2 className="font-display text-title font-semibold">{messages.contact.successTitle}</h2>
        <p className="prose-column text-body text-ink-2">{messages.contact.successBody}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <span className="font-mono text-micro uppercase text-muted" id="inquiry-type-label">
          {INQUIRY_TYPE_LABEL}
        </span>
        <div role="radiogroup" aria-labelledby="inquiry-type-label" className="flex flex-wrap gap-2">
          {TYPE_OPTIONS.map((option) => {
            const active = selectedType === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setValue("type", option.value)}
                className={cn(
                  "inline-flex h-11 items-center rounded-sm border px-4 text-caption transition-colors duration-150 ease-standard",
                  active
                    ? "border-signal bg-signal text-signal-fg"
                    : "border-edge-control bg-surface text-ink-2 hover:border-ink-2 hover:text-ink",
                )}
              >
                {messages.contact[option.labelKey]}
              </button>
            );
          })}
        </div>
      </div>

      {selectedType === "corporate" && (
        <p className="border-l-2 border-signal bg-signal-wash p-4 text-body text-ink-2">
          {messages.contact.corporateNote}
        </p>
      )}

      <Field label={messages.contact.nameLabel} htmlFor="name" error={errors.name?.message}>
        <Input id="name" autoComplete="name" aria-invalid={!!errors.name} {...register("name")} />
      </Field>

      <Field label={messages.contact.emailLabel} htmlFor="email" error={errors.email?.message}>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          aria-invalid={!!errors.email}
          {...register("email")}
        />
      </Field>

      <Field label={messages.contact.phoneLabel} htmlFor="phone" error={errors.phone?.message}>
        <Input
          id="phone"
          type="tel"
          autoComplete="tel"
          aria-invalid={!!errors.phone}
          {...register("phone")}
        />
      </Field>

      <Field label={messages.contact.messageLabel} htmlFor="message" error={errors.message?.message}>
        <Textarea id="message" aria-invalid={!!errors.message} {...register("message")} />
      </Field>

      {serverError && (
        <p role="alert" className="border-l-2 border-danger bg-raised p-4 text-body text-danger">
          {serverError}
        </p>
      )}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-fit">
        {isSubmitting ? messages.contact.submitting : messages.contact.submit}
      </Button>
    </form>
  );
}
